/**
 * DTemplate
 * A draft deck begun on the template of Distributional's seed deck (July
 * 2023), told as a seed deck's beats: each slide the answer to the
 * question in its chip. `myceo` opens it internally, with no watermark;
 * each recipient below has a password of their own, watermarked with
 * their name.
 */

import type { DeckEdition } from '@components/press/pitch-deck/pitch-deck.types';
import { HowWeBuildSlide } from '@components/press/pitch-deck/slides';
import { colors } from '@config/colors';
import {
  DAppendixSlide,
  DBusinessModelSlide,
  DClosingSlide,
  DConclusionSlide,
  DDualIncomeSlide,
  DEdgeSlide,
  DFlywheelSlide,
  DFundraiseSlide,
  DGtmSlide,
  DLandscapeSlide,
  DMarketMathSlide,
  DMarketSlide,
  DOtherExamplesSlide,
  DPersonasSlide,
  DPlanSlide,
  DProblemSlide,
  DProductSlide,
  DRoadmapSlide,
  DSolutionSlide,
  DTeamSlide,
  DViralSlide,
  DVisionSlide,
  DWhyAnotherAppSlide,
  DWhyNotRivalsSlide,
  DWhyNowSlide,
  EXAMPLE_SLIDES,
  withQuestion,
} from '@components/press/pitch-deck/slides/dtemplate';

export const DTEMPLATE: DeckEdition = {
  meta: { title: 'The Home CEO', subtitle: 'DTemplate', date: 'Draft · October 2026' },
  passwords: {
    myceo: '', // internal — no watermark
    mccourt: 'McCourt',
    carollo: 'Carollo',
  },
  label: 'DTemplate: The Home CEO',
  tocTitle: 'DTemplate',
  // June, 2026-10-07: "use a more neutral background for the flat slides
  // like a very light dove grey in the highlight family" — slate 100, the
  // lightest of the slate greys the highlight family shares.
  ground: colors.neutral.ultraLight,
  // No PDF/PPTX: export it as the Pre-Seed deck is, in capture mode:
  //   --url "http://localhost:5173/press/dtemplate?capture=1#"
  // The main deck is a seed deck's beats in order, one proposition a slide
  // (the solution in two: the loop, then its edge over AI assistants; the
  // product beside them): the first half the case for betting on the
  // category, the second for betting on us, and last the bet itself (June,
  // 2026-10-14: what we have, the three loops, the 2027 race). The
  // appendix answers the questions that are coming, a slide each, and
  // never repeats the main deck.
  slides: [
    // 1 · Vision — the site's first page, with the vision line on it.
    {
      id: 'cover',
      title: 'The Home CEO',
      category: 'main',
      backdrop: 'water',
      component: withQuestion(DVisionSlide, 'Who are you, in one line?'),
    },
    { id: 'problem', title: 'Problem', category: 'main', component: DProblemSlide },
    { id: 'solution', title: 'Solution', category: 'main', component: DSolutionSlide },
    { id: 'edge', title: 'Our Edge', category: 'main', component: DEdgeSlide },
    { id: 'product', title: 'Product', category: 'main', component: DProductSlide },
    { id: 'why-now', title: 'Why Now', category: 'main', component: DWhyNowSlide },
    { id: 'market', title: 'Market', category: 'main', component: DMarketSlide },
    { id: 'team', title: 'Team', category: 'main', component: DTeamSlide },
    { id: 'landscape', title: 'Landscape', category: 'main', component: DLandscapeSlide },
    { id: 'gtm', title: 'Go-to-Market', category: 'main', component: DGtmSlide },
    { id: 'plan', title: 'Plan', category: 'main', component: DPlanSlide },
    { id: 'fundraise', title: 'Fundraise', category: 'main', component: DFundraiseSlide },
    { id: 'conclusion', title: 'Conclusion', category: 'main', component: DConclusionSlide },
    // The table of contents opens the appendix on this id.
    { id: 'appendix-overview', title: 'Appendix', category: 'appendix', component: DAppendixSlide },
    // How we build: the Solution's loop, shown on one wish.
    {
      id: 'how-we-build',
      title: 'How We Build',
      category: 'appendix',
      component: withQuestion(HowWeBuildSlide, 'How does every wish make it better for the next mom?'),
    },
    { id: 'flywheel', title: 'Flywheel', category: 'appendix', component: DFlywheelSlide },
    ...EXAMPLE_SLIDES.map(({ demo, component }) => ({
      id: `example-${demo.key}`,
      title: `Example: ${demo.title}`,
      category: 'appendix' as const,
      component,
    })),
    { id: 'other-examples', title: 'Next Wishes', category: 'appendix', component: DOtherExamplesSlide },
    { id: 'why-another-app', title: 'Why Another App', category: 'appendix', component: DWhyAnotherAppSlide },
    { id: 'market-math', title: 'Market Math', category: 'appendix', component: DMarketMathSlide },
    { id: 'dual-income', title: 'Why Dual-Income Moms', category: 'appendix', component: DDualIncomeSlide },
    { id: 'personas', title: 'Personas and Jobs', category: 'appendix', component: DPersonasSlide },
    { id: 'why-not-them', title: 'Why Not Them', category: 'appendix', component: DWhyNotRivalsSlide },
    { id: 'virality', title: 'Virality', category: 'appendix', component: DViralSlide },
    { id: 'business-model', title: 'Business Model', category: 'appendix', component: DBusinessModelSlide },
    { id: 'roadmap', title: 'Layers and Timing', category: 'appendix', component: DRoadmapSlide },
    { id: 'closing', title: 'The Home CEO', category: 'appendix', component: DClosingSlide },
  ],
};
